import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { getAllUsers, createUser, updateUser } from "../../business/UserBusiness";
import { AuthContext } from "../../context/Auth";
import {
  Search, Plus, Pencil, Crown, Trash2, Upload, Download, RefreshCw, Users, UserPlus, BadgeCheck, Sparkles,
  CalendarClock, MoonStar, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";
import * as XLSX from "xlsx";
import { AppShell, headerButtonClass, headerPrimaryButtonClass } from '../../components/Layout/AppShell';
import { EnterprisePlanModal } from '../../components/EnterprisePlanModal';
import { ImportMinhaAgendaModal } from '../../components/ImportMinhaAgendaModal';
import { UserDetailsDrawer } from '../../components/UserDetailsDrawer';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { PlanBadge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatTile, percentChange } from '../../components/ui/StatTile';
import { Panel } from '../../components/ui/Panel';
import { SegmentedTabs } from '../../components/ui/SegmentedTabs';
import { Pagination } from '../../components/ui/Pagination';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { PLAN_META, getEffectivePlan, getPlanSourceLabel } from '../../lib/planMeta';
import {
  BUSINESS_AREA_LABELS, ACQUISITION_LABELS, ROLE_LABELS, countOf, isPayingSubscriber, hasActiveOverride,
  isInactiveAccount, getRenewalDate, getPaymentLabel, isWithinDays, formatDate, formatRelative, formatNumber, formatPhone,
} from '../../lib/userInsights';
import { updatePlanOverride, adminDeleteUser, updateDispatchConfig, createAmbassador } from "../../business/AdminBusiness";
import { CheckboxField } from "../../components/ui/CheckboxField";
import { normalizeReferralCode, isValidReferralCode } from "../../lib/referralCode";
import { ensureCountryCode } from "../../lib/phone";
import { canDeleteUsers } from "../../lib/protectedAccounts";
import toast from "react-hot-toast";

const PAGE_SIZE = 12;
const DAY = 86400000;

// Recortes da lista — cada um vira uma pílula com contagem e alguns batem com um card de KPI
const SEGMENTS = [
  { key: 'all', label: 'Todos', test: () => true },
  { key: 'new', label: 'Novos (7 dias)', test: (p) => isWithinDays(p.createdAt, 7, 0) },
  { key: 'paying', label: 'Assinantes', test: isPayingSubscriber },
  { key: 'renewing', label: 'Renovam em 7 dias', test: (p) => { const d = getRenewalDate(p); return Boolean(d) && d.getTime() >= Date.now() && d.getTime() < Date.now() + 7 * DAY; } },
  { key: 'manual', label: 'Plano manual', test: hasActiveOverride },
  { key: 'free', label: 'Free', test: (p) => getEffectivePlan(p) === 'free' },
  { key: 'inactive', label: 'Sem agendamentos', test: isInactiveAccount },
  { key: 'ambassadors', label: 'Embaixadoras', test: (p) => p.isAmbassador === true },
];

const filterSelectClass = "rounded-xl border border-gray-200 bg-white py-2 pl-3 pr-8 text-[13px] text-gray-700 shadow-card focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15";

const SortHeader = ({ label, field, sortBy, sortOrder, onSort, className = '' }) => {
  const active = sortBy === field;
  const Icon = !active ? ArrowUpDown : sortOrder === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 text-left text-[13px] font-medium text-gray-500 ${className}`}>
      <button type="button" onClick={() => onSort(field)} className={`inline-flex items-center gap-1 transition hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}>
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? 'text-accent' : 'text-gray-300'}`} />
      </button>
    </th>
  );
};

const SkeletonRows = () =>
  Array.from({ length: 6 }).map((_, i) => (
    <tr key={i} className="animate-pulse">
      <td className="px-4 py-4"><div className="flex items-center gap-3"><div className="h-9 w-9 rounded-full bg-gray-100" /><div className="space-y-1.5"><div className="h-3 w-28 rounded bg-gray-100" /><div className="h-2.5 w-36 rounded bg-gray-100" /></div></div></td>
      <td className="hidden px-4 py-4 xl:table-cell"><div className="h-3 w-24 rounded bg-gray-100" /></td>
      <td className="hidden px-4 py-4 lg:table-cell"><div className="h-3 w-16 rounded bg-gray-100" /></td>
      <td className="px-4 py-4"><div className="h-5 w-16 rounded-full bg-gray-100" /></td>
      <td className="hidden px-4 py-4 sm:table-cell"><div className="h-5 w-14 rounded-full bg-gray-100" /></td>
      <td className="hidden px-4 py-4 2xl:table-cell"><div className="h-3 w-16 rounded bg-gray-100" /></td>
      <td className="hidden px-4 py-4 md:table-cell"><div className="h-3 w-20 rounded bg-gray-100" /></td>
      <td className="px-4 py-4" />
    </tr>
  ));

const inputClass = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const primaryButtonClass = "inline-flex items-center justify-center rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60 flex-1";
const secondaryButtonClass = "rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex-1";

// Controla o prazo de um planOverride manual (dias) — usado tanto na criação quanto na edição
// de usuário, sempre junto do select de plano. Vencido o prazo, o override some sozinho
// (getEffectivePlan ignora e cai pro plano real) — não precisa vir aqui apagar nada.
const PlanOverrideDurationPicker = ({ expiresAt, onChange }) => {
  const daysRemaining = expiresAt
    ? Math.max(1, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000))
    : '';

  const setDays = (days) => {
    if (!days) {
      onChange(null);
      return;
    }
    const next = new Date();
    next.setDate(next.getDate() + Number(days));
    onChange(next.toISOString());
  };

  return (
    <div className="mt-3">
      <label className={labelClass}>Duração do plano manual</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {[{ label: '1 semana', days: 7 }, { label: '1 mês', days: 30 }, { label: '1 ano', days: 365 }].map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => setDays(opt.days)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          Sem prazo
        </button>
      </div>
      <input
        type="number"
        min="1"
        placeholder="Dias personalizados"
        value={daysRemaining}
        onChange={(e) => setDays(e.target.value)}
        className={inputClass}
      />
      <p className="text-xs text-gray-400 mt-1">
        {expiresAt
          ? `Expira em ${new Date(expiresAt).toLocaleDateString('pt-BR')} — depois disso volta pro plano real (assinatura/RevenueCat/Kiwify) automaticamente.`
          : 'Sem prazo definido: o plano manual vale até um admin trocar.'}
      </p>
    </div>
  );
};

const EMPTY_NEW_USER = {
  username: '',
  email: '',
  phone: '',
  password: '',
  role: 'user',
  planOverride: '',
  planOverrideExpiresAt: null,
  dispatchesDisabled: false,
  isAmbassador: false,
  ambassadorCode: ''
};

export const Home = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [segment, setSegment] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [enterpriseConfigUser, setEnterpriseConfigUser] = useState(null)
  const [importUser, setImportUser] = useState(null)
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER)
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt', 'username', 'usage'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' ou 'desc'

  const { user } = useContext(AuthContext);

  const fetchUsers = useCallback(async () => {
    try {
      setIsFetching(true);
      const response = await getAllUsers();
      setUsers(response?.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.log(err);
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Volta pra página 1 sempre que o recorte muda
  useEffect(() => {
    setPage(1);
  }, [searchTerm, segment, roleFilter, planFilter, sortBy, sortOrder]);

  const confirmDeleteUser = async (userId) => {
    try {
      await adminDeleteUser(userId);
      toast.success('Usuário foi removido com sucesso!');
      setDeleteTarget(null);
      setSelectedPerson(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir conta do usuário:', error);
      toast.error('Erro ao deletar conta de usuário')
    }
  };

  const isProtected = (person) => person.email === 'alexandresaintss@gmail.com' && user?.email !== 'alexandresaintss@gmail.com';
  const canDelete = canDeleteUsers(user?.email);

  // KPIs do topo — tudo calculado da lista que já vem de GET / (contagens são os arrays de id)
  const stats = useMemo(() => {
    const total = users.length;
    const new7 = users.filter((p) => isWithinDays(p.createdAt, 7, 0)).length;
    const prev7 = users.filter((p) => isWithinDays(p.createdAt, 14, 7)).length;
    const new30 = users.filter((p) => isWithinDays(p.createdAt, 30, 0)).length;
    const prev30 = users.filter((p) => isWithinDays(p.createdAt, 60, 30)).length;
    const paying = users.filter(isPayingSubscriber).length;
    const manual = users.filter(hasActiveOverride);
    const manualExpiring = manual.filter((p) => p.planOverrideExpiresAt && new Date(p.planOverrideExpiresAt).getTime() < Date.now() + 7 * DAY).length;
    const renewing = users.filter(SEGMENTS.find((s) => s.key === 'renewing').test).length;
    const inactive = users.filter(isInactiveAccount).length;
    return { total, new7, prev7, new30, prev30, paying, manual: manual.length, manualExpiring, renewing, inactive };
  }, [users]);

  const segmentCounts = useMemo(
    () => Object.fromEntries(SEGMENTS.map((s) => [s.key, users.filter(s.test).length])),
    [users]
  );

  const sortedUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const digits = term.replace(/\D/g, '');
    const segmentTest = SEGMENTS.find((s) => s.key === segment)?.test || (() => true);

    const filtered = users.filter((person) => {
      const matchesSearch = !term ||
        person?.username?.toLowerCase().includes(term) ||
        person?.email?.toLowerCase().includes(term) ||
        person?.businessName?.toLowerCase().includes(term) ||
        (digits.length >= 4 && String(person?.phone || '').includes(digits));
      const matchesRole = roleFilter === "all" || person?.role === roleFilter;
      const matchesPlan = planFilter === "all" || getEffectivePlan(person) === planFilter;
      return matchesSearch && matchesRole && matchesPlan && segmentTest(person);
    });

    const dir = sortOrder === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      if (sortBy === 'username') return dir * (a.username || '').localeCompare(b.username || '', 'pt-BR');
      if (sortBy === 'usage') return dir * (countOf(a, 'appointments') - countOf(b, 'appointments'));
      return dir * (new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    });
  }, [users, searchTerm, segment, roleFilter, planFilter, sortBy, sortOrder]);

  const pageUsers = sortedUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasExtraFilters = searchTerm || roleFilter !== 'all' || planFilter !== 'all' || segment !== 'all';

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'username' ? 'asc' : 'desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setPlanFilter('all');
    setSegment('all');
  };

  // Exporta o recorte atual (respeita busca, pílula e filtros) pra planilha
  const handleExport = () => {
    const rows = sortedUsers.map((p) => ({
      Nome: p.username || '',
      'E-mail': p.email || '',
      Telefone: formatPhone(p.phone),
      Negócio: p.businessName || '',
      Áreas: (p.businessAreas || []).map((a) => BUSINESS_AREA_LABELS[a] || a).join(', '),
      Plano: PLAN_META[getEffectivePlan(p)]?.label || '',
      'Origem do plano': getPlanSourceLabel(p),
      Assinatura: p.subscription === 'active' ? 'Ativa' : 'Inativa',
      'Pagamento via': getPaymentLabel(p) || '',
      'Próxima renovação': getRenewalDate(p) ? formatDate(getRenewalDate(p), { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
      Agendamentos: countOf(p, 'appointments'),
      Clientes: countOf(p, 'clients'),
      Lançamentos: countOf(p, 'transactions'),
      'Conheceu por': ACQUISITION_LABELS[p.acquisitionSource] || '',
      'Código usado': p.referralCodeUsed || '',
      Cargo: ROLE_LABELS[p.role] || p.role || '',
      'Criado em': p.createdAt ? new Date(p.createdAt).toLocaleDateString('pt-BR') : '',
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Usuários');
    XLSX.writeFile(book, `agendix-usuarios-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${rows.length} usuário(s) exportado(s)`);
  };

  const handleEditClick = (person) => {
    setEditingUser({
      _id: person._id,
      username: person.username,
      email: person.email || '',
      phone: person.phone || '',
      role: person.role || 'user',
      subscription: person.subscription || 'inactive',
      planOverride: person.planOverride ?? '',
      planOverrideExpiresAt: person.planOverrideExpiresAt ?? null,
      dispatchesDisabled: person.dispatchesDisabled === true,
      originalDispatchesDisabled: person.dispatchesDisabled === true
    });
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleEditUser = async () => {
    try {
      if (!editingUser.email || !editingUser.phone) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      setIsSaving(true);

      const updateData = {
        email: editingUser.email,
        phone: editingUser.phone,
        role: editingUser.role,
        subscription: editingUser.subscription
      };

      await updateUser(editingUser._id, updateData);

      const planOverrideValue = editingUser.planOverride === '' ? null : editingUser.planOverride;
      await updatePlanOverride(editingUser._id, planOverrideValue, editingUser.planOverrideExpiresAt);
      if (editingUser.dispatchesDisabled !== editingUser.originalDispatchesDisabled) {
        await updateDispatchConfig(editingUser._id, editingUser.dispatchesDisabled);
      }
      toast.success('Usuário atualizado com sucesso!');

      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      toast.error(error?.response?.data?.msg || 'Erro ao atualizar usuário');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.username || !newUser.email || !newUser.phone || !newUser.password) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }
      if (newUser.isAmbassador && !isValidReferralCode(newUser.ambassadorCode)) {
        toast.error('Código da embaixadora inválido: use de 3 a 20 letras ou números.');
        return;
      }

      setIsSaving(true);
      const { dispatchesDisabled, isAmbassador, ambassadorCode, ...registerPayload } = newUser;
      const res = await createUser({ ...registerPayload, phone: ensureCountryCode(newUser.phone) });
      const createdId = res?.data?.user?._id || res?.data?._id;
      if (createdId && newUser.planOverride) {
        await updatePlanOverride(createdId, newUser.planOverride, newUser.planOverrideExpiresAt);
      }

      // Embaixadora: um endpoint só já grava o código e o "sem disparos". Se falhar (ex.: código
      // repetido) a conta já existe — avisa e manda cadastrar pela tela Embaixadoras.
      let ambassadorFailure = null;
      if (createdId && isAmbassador) {
        try {
          await createAmbassador({ userId: createdId, code: ambassadorCode, disableDispatches: dispatchesDisabled });
        } catch (ambassadorError) {
          ambassadorFailure = ambassadorError?.response?.data?.msg || 'erro ao cadastrar a embaixadora';
        }
      } else if (createdId && dispatchesDisabled) {
        await updateDispatchConfig(createdId, true);
      }

      if (ambassadorFailure) {
        toast.error(`Usuário criado, mas a embaixadora não foi cadastrada: ${ambassadorFailure}. Cadastre pela tela Embaixadoras.`, { duration: 8000 });
      } else {
        toast.success('Usuário criado com sucesso!');
      }

      setShowCreateModal(false);
      setNewUser(EMPTY_NEW_USER);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error(error?.response?.data?.msg || 'Erro ao criar usuário');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const blockedTitle = 'Somente o próprio Alexandre pode gerenciar esta conta';

  const rowActionsFor = (person) => {
    const blocked = isProtected(person);
    const actions = [
      { key: 'edit', label: 'Editar', icon: Pencil, onClick: () => handleEditClick(person), disabled: blocked, disabledTitle: blockedTitle },
      { key: 'enterprise', label: 'Configurar Enterprise', icon: Crown, onClick: () => setEnterpriseConfigUser(person), disabled: blocked, disabledTitle: blockedTitle },
      { key: 'import', label: 'Importar do Minha Agenda', icon: Upload, onClick: () => setImportUser(person), disabled: blocked, disabledTitle: blockedTitle },
    ];
    // Excluir só aparece pra quem tem permissão — a trava de verdade é no backend
    // (requireUserDeletePermission), isso aqui é só pra não oferecer um botão que vai 403.
    if (canDelete) {
      actions.push({ key: 'delete', label: 'Excluir', icon: Trash2, danger: true, onClick: () => setDeleteTarget(person), disabled: blocked, disabledTitle: blockedTitle });
    }
    return actions;
  };

  const drawerFooter = (person) => {
    const blocked = isProtected(person);
    const iconButton = "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40";
    return (
      <div className="flex gap-2">
        <button
          className={`${headerPrimaryButtonClass} flex-1`}
          disabled={blocked}
          title={blocked ? blockedTitle : undefined}
          onClick={() => { setSelectedPerson(null); handleEditClick(person); }}
        >
          <Pencil className="h-4 w-4" /> Editar usuário
        </button>
        <button className={iconButton} disabled={blocked} title="Configurar Enterprise" onClick={() => { setSelectedPerson(null); setEnterpriseConfigUser(person); }}>
          <Crown className="h-4 w-4" />
        </button>
        <button className={iconButton} disabled={blocked} title="Importar do Minha Agenda" onClick={() => { setSelectedPerson(null); setImportUser(person); }}>
          <Upload className="h-4 w-4" />
        </button>
        {canDelete && (
          <button className={`${iconButton} hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600`} disabled={blocked} title="Excluir" onClick={() => setDeleteTarget(person)}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const conversion = stats.total ? Math.round((stats.paying / stats.total) * 100) : 0;
  const inactivePct = stats.total ? Math.round((stats.inactive / stats.total) * 100) : 0;
  const pickSegment = (key) => setSegment((current) => (current === key ? 'all' : key));

  return (
    <>
      <AppShell
        icon={Users}
        eyebrow="Visão geral"
        title="Usuários"
        meta={lastUpdated ? <>Atualizado às <span className="font-semibold text-gray-700">{lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span></> : 'Carregando...'}
        actions={
          <>
            <button className={headerButtonClass} onClick={handleExport} disabled={isFetching || !sortedUsers.length}>
              <Download className="h-4 w-4" /> Exportar
            </button>
            <button className={headerButtonClass} onClick={fetchUsers} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Atualizar
            </button>
            <button className={headerPrimaryButtonClass} onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4" /> Novo usuário
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <Panel title="Visão rápida" subtitle="Clique num card para filtrar a lista">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6">
              <StatTile icon={Users} label="Total de usuários" value={formatNumber(stats.total)} trend={percentChange(stats.new7, stats.prev7)} caption={`+${stats.new7} em 7 dias`} onClick={() => setSegment('all')} active={segment === 'all'} />
              <StatTile icon={UserPlus} label="Novos no mês" value={formatNumber(stats.new30)} trend={percentChange(stats.new30, stats.prev30)} caption="vs 30 dias antes" onClick={() => pickSegment('new')} active={segment === 'new'} />
              <StatTile icon={BadgeCheck} label="Assinantes" value={formatNumber(stats.paying)} caption={`${conversion}% de conversão`} onClick={() => pickSegment('paying')} active={segment === 'paying'} />
              <StatTile icon={CalendarClock} label="Renovam em 7 dias" value={formatNumber(stats.renewing)} caption="assinaturas a cobrar" onClick={() => pickSegment('renewing')} active={segment === 'renewing'} />
              <StatTile icon={Sparkles} label="Plano manual" value={formatNumber(stats.manual)} caption={stats.manualExpiring ? `${stats.manualExpiring} vencem em 7 dias` : 'nenhum vencendo'} onClick={() => pickSegment('manual')} active={segment === 'manual'} />
              <StatTile icon={MoonStar} label="Sem agendamentos" value={formatNumber(stats.inactive)} caption={`${inactivePct}% das contas · ativar`} onClick={() => pickSegment('inactive')} active={segment === 'inactive'} />
            </div>
          </Panel>

          <Panel
            title="Todos os usuários"
            subtitle={hasExtraFilters ? `${sortedUsers.length} de ${users.length} no filtro atual` : `${users.length} contas cadastradas`}
            bodyClassName="p-0"
            actions={
              <>
                <div className="relative w-full sm:w-64">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-[13px] shadow-card placeholder-gray-400 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15"
                    placeholder="Buscar nome, e-mail, telefone..."
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                  />
                </div>
                <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className={filterSelectClass} aria-label="Filtrar por plano">
                  <option value="all">Todos os planos</option>
                  {Object.keys(PLAN_META).map((plan) => (
                    <option key={plan} value={plan}>{PLAN_META[plan]?.label || plan}</option>
                  ))}
                </select>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={filterSelectClass} aria-label="Filtrar por cargo">
                  <option value="all">Todos os cargos</option>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </>
            }
          >
            <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
              <SegmentedTabs
                value={segment}
                onChange={setSegment}
                tabs={SEGMENTS.map((s) => ({ key: s.key, label: s.label, count: segmentCounts[s.key] }))}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <SortHeader label="Usuário" field="username" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} />
                    <th className="hidden px-4 py-3 text-left text-[13px] font-medium text-gray-500 xl:table-cell">Negócio</th>
                    <SortHeader label="Uso" field="usage" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} className="hidden lg:table-cell" />
                    <th className="px-4 py-3 text-left text-[13px] font-medium text-gray-500">Plano</th>
                    <th className="hidden px-4 py-3 text-left text-[13px] font-medium text-gray-500 sm:table-cell">Assinatura</th>
                    <th className="hidden px-4 py-3 text-left text-[13px] font-medium text-gray-500 2xl:table-cell">Origem</th>
                    <SortHeader label="Cadastro" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} className="hidden md:table-cell" />
                    <th className="px-4 py-3 text-right text-[13px] font-medium text-gray-500"><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isFetching && !users.length ? (
                    <SkeletonRows />
                  ) : pageUsers.length > 0 ? (
                    pageUsers.map((person) => {
                      const plan = getEffectivePlan(person);
                      const appointments = countOf(person, 'appointments');
                      const clients = countOf(person, 'clients');
                      const areas = (person.businessAreas || []).map((a) => BUSINESS_AREA_LABELS[a] || a);
                      const payment = getPaymentLabel(person);
                      return (
                        <tr
                          key={person?._id}
                          className="cursor-pointer transition-colors hover:bg-subtle"
                          onClick={() => setSelectedPerson(person)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar name={person?.username} />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="max-w-[180px] truncate text-sm font-medium text-gray-900">{person?.username}</p>
                                  {person?.isAmbassador === true && <StatusPill tone="violet" className="!px-1.5 !py-0 text-[10px]">Embaixadora</StatusPill>}
                                  {person?.dispatchesDisabled === true && <StatusPill tone="amber" className="!px-1.5 !py-0 text-[10px]">Sem disparos</StatusPill>}
                                  {person?.role && person.role !== 'user' && <StatusPill tone={person.role === 'admin' ? 'red' : 'blue'} className="!px-1.5 !py-0 text-[10px]">{ROLE_LABELS[person.role] || person.role}</StatusPill>}
                                </div>
                                <p className="max-w-[220px] truncate text-xs text-gray-500">{person?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="hidden px-4 py-3 xl:table-cell">
                            <p className="max-w-[180px] truncate text-sm text-gray-800">{person?.businessName || <span className="text-gray-400">—</span>}</p>
                            {areas.length > 0 && (
                              <p className="max-w-[180px] truncate text-xs text-gray-500">{areas.slice(0, 2).join(' · ')}{areas.length > 2 ? ` +${areas.length - 2}` : ''}</p>
                            )}
                          </td>
                          <td className="hidden whitespace-nowrap px-4 py-3 lg:table-cell">
                            <p className={`tabular text-sm ${appointments ? 'text-gray-900' : 'text-gray-400'}`}>{formatNumber(appointments)} <span className="text-gray-500">agend.</span></p>
                            <p className="tabular text-xs text-gray-500">{formatNumber(clients)} {clients === 1 ? 'cliente' : 'clientes'}</p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <PlanBadge plan={plan} meta={PLAN_META} />
                            {hasActiveOverride(person) && <p className="mt-0.5 text-[11px] text-gray-400">{getPlanSourceLabel(person).replace('Manual · ', 'Manual ')}</p>}
                          </td>
                          <td className="hidden whitespace-nowrap px-4 py-3 sm:table-cell">
                            <StatusPill tone={person?.subscription === 'active' ? 'green' : 'gray'}>{person?.subscription === 'active' ? 'Ativa' : 'Inativa'}</StatusPill>
                            {person?.subscription === 'active' && payment && <p className="mt-0.5 text-[11px] text-gray-400">via {payment}</p>}
                          </td>
                          <td className="hidden whitespace-nowrap px-4 py-3 text-sm text-gray-700 2xl:table-cell">
                            {person?.referralCodeUsed ? (
                              <span className="rounded-md bg-accent-light px-1.5 py-0.5 font-mono text-xs text-accent">{person.referralCodeUsed}</span>
                            ) : ACQUISITION_LABELS[person?.acquisitionSource] || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="hidden whitespace-nowrap px-4 py-3 md:table-cell">
                            <p className="tabular text-sm text-gray-800">{formatDate(person?.createdAt)}</p>
                            <p className="text-xs text-gray-500">{formatRelative(person?.createdAt)}</p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <RowActionsMenu actions={rowActionsFor(person)} />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-5 py-14 text-center">
                        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-subtle text-gray-400"><Search className="h-5 w-5" /></div>
                        <p className="mt-3 text-sm font-medium text-gray-900">Nenhum usuário encontrado</p>
                        <p className="mt-1 text-sm text-gray-500">Tente outro termo ou limpe os filtros.</p>
                        {hasExtraFilters && (
                          <button onClick={clearFilters} className="mt-4 text-sm font-medium text-accent hover:underline">Limpar filtros</button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-100">
              <Pagination page={page} pageSize={PAGE_SIZE} total={sortedUsers.length} onChange={setPage} noun="usuários" />
            </div>
          </Panel>
        </div>
      </AppShell>

      {/* Modal de criação de usuário */}
      <Modal title="Criar Novo Usuário" isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="space-y-4">
          <div>
            <label className={labelClass} htmlFor="username">Nome *</label>
            <input className={inputClass} id="username" type="text" placeholder="joao_silva" value={newUser.username} onChange={(e) => handleInputChange('username', e.target.value)} />
          </div>
          <div>
            <label className={labelClass} htmlFor="email">Email *</label>
            <input className={inputClass} id="email" type="email" placeholder="joao@email.com" value={newUser.email} onChange={(e) => handleInputChange('email', e.target.value)} />
          </div>
          <div>
            <label className={labelClass} htmlFor="phone">Telefone *</label>
            <input className={inputClass} id="phone" type="tel" placeholder="(11) 99999-9999" value={newUser.phone} onChange={(e) => handleInputChange('phone', e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">Lembre-se do código do país na frente</p>
          </div>
          <div>
            <label className={labelClass} htmlFor="password">Senha *</label>
            <input className={inputClass} id="password" type="password" placeholder="********" value={newUser.password} onChange={(e) => handleInputChange('password', e.target.value)} />
          </div>
          <div>
            <label className={labelClass} htmlFor="role">Cargo *</label>
            <select className={inputClass} id="role" value={newUser.role} onChange={(e) => handleInputChange('role', e.target.value)}>
              <option value="user">Usuário</option>
              <option value="admin">Administrador</option>
              <option value="patner">Parceiro</option>
              <option value="trial">Trial</option>
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="create-plan-override">Plano manual</label>
            <select className={inputClass} id="create-plan-override" value={newUser.planOverride} onChange={(e) => handleInputChange('planOverride', e.target.value)}>
              <option value="">Free (padrão)</option>
              <option value="start">Start</option>
              <option value="pro">Pro</option>
              <option value="studio">Studio</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Sobrescreve o plano padrão do usuário para testes ou acessos personalizados.</p>
            {newUser.planOverride && (
              <PlanOverrideDurationPicker
                expiresAt={newUser.planOverrideExpiresAt}
                onChange={(value) => handleInputChange('planOverrideExpiresAt', value)}
              />
            )}
          </div>
          <div className="space-y-2">
            <CheckboxField
              id="create-dispatches-disabled"
              checked={newUser.dispatchesDisabled}
              onChange={(value) => handleInputChange('dispatchesDisabled', value)}
              label="Sem disparos (só push)"
              help="Não envia WhatsApp nem e-mail (confirmações, lembretes, aniversário, relatórios). O push do app continua. Por padrão toda conta tem disparos."
            />
            <CheckboxField
              id="create-is-ambassador"
              checked={newUser.isAmbassador}
              onChange={(value) => {
                handleInputChange('isAmbassador', value);
                // Embaixadora nasce sem disparos (decisão da call de 23/09) — dá pra desmarcar.
                if (value) handleInputChange('dispatchesDisabled', true);
              }}
              label="É embaixadora"
              help="Cadastra a conta no programa de embaixadoras com o código de indicação abaixo."
            />
            {newUser.isAmbassador && (
              <div>
                <label className={labelClass} htmlFor="create-ambassador-code">Código de indicação *</label>
                <input
                  className={`${inputClass} uppercase`}
                  id="create-ambassador-code"
                  type="text"
                  placeholder="GABI10"
                  maxLength={20}
                  value={newUser.ambassadorCode}
                  onChange={(e) => handleInputChange('ambassadorCode', normalizeReferralCode(e.target.value))}
                />
                <p className="text-xs text-gray-400 mt-1">3 a 20 letras ou números, sem espaços. Sempre em maiúsculas e único por embaixadora.</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <button className={primaryButtonClass} onClick={handleCreateUser} disabled={isSaving}>
              {isSaving ? 'Criando...' : 'Criar Usuário'}
            </button>
            <button className={secondaryButtonClass} onClick={() => setShowCreateModal(false)} disabled={isSaving}>
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de edição de usuário */}
      <Modal title="Editar Usuário" isOpen={showEditModal && !!editingUser} onClose={handleEditModalClose}>
        {editingUser && (
          <div className="space-y-4">
            <div>
              <label className={labelClass} htmlFor="edit-username">Nome</label>
              <input className={`${inputClass} bg-gray-100`} id="edit-username" type="text" value={editingUser.username} disabled />
              <p className="text-xs text-gray-400 mt-1">Username não pode ser alterado</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="edit-email">Email *</label>
              <input className={inputClass} id="edit-email" type="email" placeholder="joao@email.com" value={editingUser.email} onChange={(e) => handleEditInputChange('email', e.target.value)} />
            </div>
            <div>
              <label className={labelClass} htmlFor="edit-phone">Telefone *</label>
              <input className={inputClass} id="edit-phone" type="tel" placeholder="(11) 99999-9999" value={editingUser.phone} onChange={(e) => handleEditInputChange('phone', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Lembre-se do código do país na frente</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="edit-role">Cargo *</label>
              <select className={inputClass} id="edit-role" value={editingUser.role} onChange={(e) => handleEditInputChange('role', e.target.value)}>
                <option value="user">Usuário</option>
                <option value="admin">Administrador</option>
                <option value="patner">Parceiro</option>
                <option value="trial">Trial</option>
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="edit-subscription">Status de assinatura *</label>
              <select className={inputClass} id="edit-subscription" value={editingUser.subscription} onChange={(e) => handleEditInputChange('subscription', e.target.value)}>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="edit-plan-override">Plano manual</label>
              <select className={inputClass} id="edit-plan-override" value={editingUser.planOverride ?? ''} onChange={(e) => handleEditInputChange('planOverride', e.target.value)}>
                <option value="">Nenhum (padrão pelo plano contratado)</option>
                <option value="free">Free</option>
                <option value="start">Start</option>
                <option value="pro">Pro</option>
                <option value="studio">Studio</option>
                <option value="business">Business</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Sobrescreve o plano atual do usuário para testes ou acessos personalizados.</p>
              {editingUser.planOverride && (
                <PlanOverrideDurationPicker
                  expiresAt={editingUser.planOverrideExpiresAt}
                  onChange={(value) => handleEditInputChange('planOverrideExpiresAt', value)}
                />
              )}
            </div>
            <CheckboxField
              id="edit-dispatches-disabled"
              checked={editingUser.dispatchesDisabled === true}
              onChange={(value) => handleEditInputChange('dispatchesDisabled', value)}
              label="Sem disparos (só push)"
              help="Não envia WhatsApp nem e-mail (confirmações, lembretes, aniversário, relatórios). O push do app continua. Cuidado ao marcar em conta de cliente pagante."
            />
            <div className="flex gap-2 pt-2">
              <button className={primaryButtonClass} onClick={handleEditUser} disabled={isSaving}>
                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
              <button className={secondaryButtonClass} onClick={handleEditModalClose} disabled={isSaving}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Importação da planilha do Minha Agenda (só adiciona) */}
      {importUser && (
        <ImportMinhaAgendaModal user={importUser} onClose={() => setImportUser(null)} onDone={fetchUsers} />
      )}

      {/* Modal de configuração do plano enterprise */}
      {enterpriseConfigUser && (
        <EnterprisePlanModal
          user={enterpriseConfigUser}
          onClose={() => setEnterpriseConfigUser(null)}
          onSaved={(updatedUser) => {
            if (updatedUser) {
              setUsers((prev) => prev.map((u) => (u._id === updatedUser._id ? updatedUser : u)));
            }
            setEnterpriseConfigUser(null);
          }}
        />
      )}

      <UserDetailsDrawer person={selectedPerson} onClose={() => setSelectedPerson(null)} footer={drawerFooter} />

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Excluir usuário"
        message={`Tem certeza que deseja excluir "${deleteTarget?.username}"? Essa ação não pode ser desfeita.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => confirmDeleteUser(deleteTarget?._id)}
      />
    </>
  );
};
